// src/pages/PartnerDisbursementsPage.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useData } from "../DataContext";
import { generateId } from "../utils/id";
import { PartnerDisbursement } from "../data/mockData";
import { projectDisplayLabelPretty } from "../utils/format";

import { Card } from "../components/ui/Card";
import { TableShell, CellClamp } from "../components/ui/TableShell";

type Kind = "Advance" | "Settlement" | "Other";
type Method = "Cash" | "Bank";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// lightweight inline icons (no deps)
function IconPencil({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconTrash({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M7 6l1 16h8l1-16" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function PartnerDisbursementsPage() {
  const { t } = useTranslation();
  const {
    clients,
    projects,
    partners,
    partnerDisbursements,
    addPartnerDisbursement,
    updatePartnerDisbursement,
    deletePartnerDisbursement,
  } = useData() as any;

  const projectLabel = useCallback(
    (p: any) => {
      // same display style as Dashboard
      try {
        return projectDisplayLabelPretty(p, clients);
      } catch {
        return `${p?.name || p?.id || "Project"} (${p?.id || ""})`.trim();
      }
    },
    [clients]
  );

  // -------------------------
  // Add form
  // -------------------------
  const [projectId, setProjectId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<Method>("Cash");
  const [kind, setKind] = useState<Kind>("Advance");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // -------------------------
  // Editing
  // -------------------------
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState("");
  const [editPartnerId, setEditPartnerId] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editMethod, setEditMethod] = useState<Method>("Cash");
  const [editKind, setEditKind] = useState<Kind>("Advance");
  const [editDate, setEditDate] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // -------------------------
  // Ledger controls
  // -------------------------
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<Kind | "All">("All");
  const [methodFilter, setMethodFilter] = useState<Method | "All">("All");
  const [projectFilter, setProjectFilter] = useState<string>("All");
  const [partnerFilter, setPartnerFilter] = useState<string>("All");

  // -------------------------
  // Totals
  // -------------------------
  const totalsAll = useMemo(() => {
    const sum = (arr: PartnerDisbursement[]) =>
      (arr || []).reduce((s, d: any) => s + (Number(d.amount) || 0), 0);

    const byKind = (k: Kind) =>
      (partnerDisbursements || []).filter((d: any) => (d.kind || "Advance") === k);

    return {
      advance: sum(byKind("Advance")),
      settlement: sum(byKind("Settlement")),
      other: sum(byKind("Other")),
      cash: sum((partnerDisbursements || []).filter((d: any) => d.method === "Cash")),
      bank: sum((partnerDisbursements || []).filter((d: any) => d.method === "Bank")),
    };
  }, [partnerDisbursements]);

  const filteredDisbursements = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...(partnerDisbursements || [])]
      .filter((d: any) => {
        if (kindFilter !== "All" && (d.kind || "Advance") !== kindFilter) return false;
        if (methodFilter !== "All" && d.method !== methodFilter) return false;
        if (projectFilter !== "All" && d.projectId !== projectFilter) return false;
        if (partnerFilter !== "All" && d.partnerId !== partnerFilter) return false;

        if (!q) return true;

        const proj = projects.find((p: any) => p.id === d.projectId);
        const partner = partners.find((p: any) => p.id === d.partnerId);

        const prettyProject = proj ? projectLabel(proj) : d.projectId;

        const hay = [
          d.id,
          d.date,
          d.kind,
          d.method,
          d.notes || "",
          prettyProject,
          partner?.name || d.partnerId,
          String(d.amount ?? ""),
        ]
          .join(" ")
          .toLowerCase();

        return hay.includes(q);
      })
      .sort((a: any, b: any) => (a.date < b.date ? 1 : -1));
  }, [
    partnerDisbursements,
    projects,
    projectLabel,
    partners,
    search,
    kindFilter,
    methodFilter,
    projectFilter,
    partnerFilter,
  ]);

  const totalsFiltered = useMemo(() => {
    const sum = (arr: any[]) => arr.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const byKind = (k: Kind) =>
      filteredDisbursements.filter((d: any) => (d.kind || "Advance") === k);

    return {
      advance: sum(byKind("Advance")),
      settlement: sum(byKind("Settlement")),
      other: sum(byKind("Other")),
      cash: sum(filteredDisbursements.filter((d: any) => d.method === "Cash")),
      bank: sum(filteredDisbursements.filter((d: any) => d.method === "Bank")),
      total: sum(filteredDisbursements),
    };
  }, [filteredDisbursements]);

  // -------------------------
  // Add / edit handlers
  // -------------------------
  const resetAddForm = () => {
    setProjectId("");
    setPartnerId("");
    setAmount("");
    setMethod("Cash");
    setKind("Advance");
    setDate("");
    setNotes("");
    setFormError(null);
  };

  const handleAdd = () => {
    const amt = Number(amount);

    if (!projectId || !partnerId || !amount || !date) {
      setFormError(t("partnerDisbursements.formErrorRequired"));
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      setFormError(t("partnerDisbursements.formErrorAmount"));
      return;
    }

    addPartnerDisbursement({
      id: generateId("PD"),
      projectId,
      partnerId,
      amount: amt,
      method,
      kind,
      date,
      notes: notes.trim() ? notes.trim() : undefined,
    });

    // keep project/partner selection (usually faster data entry)
    setAmount("");
    setNotes("");
    setFormError(null);
  };

  const startEdit = (d: PartnerDisbursement) => {
    const dd: any = d;
    setEditingId(dd.id);
    setEditProjectId(dd.projectId);
    setEditPartnerId(dd.partnerId);
    setEditAmount(String(dd.amount ?? ""));
    setEditMethod((dd.method as Method) || "Cash");
    setEditKind(((dd.kind as Kind) || "Advance") as Kind);
    setEditDate(dd.date || "");
    setEditNotes(dd.notes || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditProjectId("");
    setEditPartnerId("");
    setEditAmount("");
    setEditMethod("Cash");
    setEditKind("Advance");
    setEditDate("");
    setEditNotes("");
  };

  const saveEdit = (d: PartnerDisbursement) => {
    const dd: any = d;
    const amt = Number(editAmount);

    if (!editProjectId || !editPartnerId || !editAmount || !editDate) {
      alert(t("partnerDisbursements.formErrorRequiredFields"));
      return;
    }
    if (Number.isNaN(amt) || amt <= 0) {
      alert(t("partnerDisbursements.formErrorAmount"));
      return;
    }

    if (typeof updatePartnerDisbursement !== "function") {
      alert(
        "updatePartnerDisbursement is not available in DataContext yet. Add it (and delete) to enable edits."
      );
      return;
    }

    updatePartnerDisbursement({
      ...dd,
      projectId: editProjectId,
      partnerId: editPartnerId,
      amount: amt,
      method: editMethod,
      kind: editKind,
      date: editDate,
      notes: editNotes.trim() ? editNotes.trim() : undefined,
    });

    cancelEdit();
  };

  const doDelete = (d: PartnerDisbursement) => {
    const dd: any = d;

    if (typeof deletePartnerDisbursement !== "function") {
      alert("deletePartnerDisbursement is not available in DataContext yet. Add it to enable deletes.");
      return;
    }

    const proj = projects.find((p: any) => p.id === dd.projectId);
    const partner = partners.find((p: any) => p.id === dd.partnerId);

    const ok = window.confirm(
      `Delete this partner disbursement?\n\nProject: ${proj ? projectLabel(proj) : dd.projectId}\nPartner: ${
        partner?.name || dd.partnerId
      }\nKind: ${dd.kind || "Advance"}\nAmount: ${money(dd.amount)}\nDate: ${dd.date}`
    );
    if (!ok) return;

    deletePartnerDisbursement(dd.id);
    if (editingId === dd.id) cancelEdit();
  };

  // -------------------------
  // Header-right filters for ledger (ONE ROW on desktop)
  // -------------------------
  const ledgerHeaderRight = (
    <div className="pdFilters">
      <div className="pdFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          Kind
        </label>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as any)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          <option value="Advance">{t("partnerDisbursements.kindAdvance")}</option>
          <option value="Settlement">{t("partnerDisbursements.kindSettlement")}</option>
          <option value="Other">{t("common.other")}</option>
        </select>
      </div>

      <div className="pdFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          Method
        </label>
        <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value as any)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          <option value="Cash">{t("common.cash")}</option>
          <option value="Bank">{t("common.bank")}</option>
        </select>
      </div>

      <div className="pdFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          Project
        </label>
        <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          {projects.map((p: any) => (
            <option key={p.id} value={p.id}>
              {projectLabel(p)}
            </option>
          ))}
        </select>
      </div>

      <div className="pdFilterItem">
        <label className="label" style={{ marginBottom: 6 }}>
          Partner
        </label>
        <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} style={{ width: "100%" }}>
          <option value="All">{t("common.all")}</option>
          {partners.map((p: any) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {(kindFilter !== "All" ||
        methodFilter !== "All" ||
        projectFilter !== "All" ||
        partnerFilter !== "All") && (
        <button
          className="btn btnSecondary btnSm"
          type="button"
          onClick={() => {
            setKindFilter("All");
            setMethodFilter("All");
            setProjectFilter("All");
            setPartnerFilter("All");
          }}
          title={t("common.clearFilters")}
          style={{ alignSelf: "end" }}
        >
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div className="container">
      <style>{`
        /* --- card spacing --- */
        .pdStack { display: grid; gap: 14px; }

        /* --- search header responsiveness --- */
        @media (max-width: 980px) {
          .pdHideMd { display: none !important; }
          .pdRow5 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 680px) {
          .pdHideSm { display: none !important; }
          .pdRow5 { grid-template-columns: 1fr !important; }
        }
        .pdField { min-width: 0; }
        textarea.input { box-sizing: border-box; width: 100%; max-width: 100%; }

        /* --- filters: 1 row on desktop --- */
        .pdFilters{
          display: grid;
          grid-template-columns: repeat(4, minmax(140px, 1fr)) auto;
          gap: 12px;
          align-items: end;
          width: min(860px, 100%);
          justify-self: end;
        }
        .pdFilterItem{ min-width: 0; }

        @media (max-width: 980px){
          .pdFilters{
            grid-template-columns: repeat(2, minmax(140px, 1fr));
            width: 100%;
            justify-self: stretch;
          }
        }
        @media (max-width: 680px){
          .pdFilters{
            grid-template-columns: 1fr;
          }
        }

        /* --- table look --- */
        .pdLedger table { font-size: 13px; }
        .pdLedger thead th { padding: 10px 12px; }
        .pdLedger tbody td { padding: 10px 12px; }

        .tableIconBtn {
          height: 36px;
          width: 36px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: rgba(255,255,255,0.92);
          color: var(--text);
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
          transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
        }
        .tableIconBtn:hover {
          background: rgba(255,255,255,1);
          transform: translateY(-1px);
          border-color: rgba(15, 23, 42, 0.18);
        }
        .tableIconBtn:active { transform: translateY(0px); }
        .tableIconBtnDanger {
          border-color: rgba(229, 83, 61, 0.28);
          background: rgba(229, 83, 61, 0.08);
          color: var(--bad);
          box-shadow: none;
        }
        .tableIconBtnDanger:hover {
          background: rgba(229, 83, 61, 0.12);
          border-color: rgba(229, 83, 61, 0.40);
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Partner Disbursements</h1>
          <div className="pageNote">
            {t("partnerDisbursements.pageNote")}
          </div>
        </div>

        <div style={{ minWidth: 320, maxWidth: 520, flex: "1 1 360px" }}>
          <label className="label">{t("common.search")}</label>
          <input
            className="input"
            placeholder={t("partnerDisbursements.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className="pdStack">
        {/* ADD DISBURSEMENT */}
        <Card
          title={t("partnerDisbursements.addTitle")}
          subtitle={
            <>
              <span className="reqStar">*</span> {t("common.required")}
            </>
          }
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div className="field pdField">
              <label className="label">
                {t("common.project")} <span className="reqStar">*</span>
              </label>
              <select
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  if (formError) setFormError(null);
                }}
                style={{ width: "100%" }}
              >
                <option value="">{t("common.selectProject")}</option>
                {projects.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {projectLabel(p)}
                  </option>
                ))}
              </select>
            </div>

            <div
              className="pdRow5"
              style={{
                display: "grid",
                gap: 14,
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                alignItems: "end",
              }}
            >
              <div className="field pdField">
                <label className="label">
                  {t("partnerDisbursements.partner")} <span className="reqStar">*</span>
                </label>
                <select
                  value={partnerId}
                  onChange={(e) => {
                    setPartnerId(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">{t("partnerDisbursements.selectPartner")}</option>
                  {partners.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field pdField">
                <label className="label">
                  {t("common.amount")} <span className="reqStar">*</span>
                </label>
                <input
                  className="input"
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="field pdField">
                <label className="label">{t("common.method")}</label>
                <select value={method} onChange={(e) => setMethod(e.target.value as Method)} style={{ width: "100%" }}>
                  <option value="Cash">{t("common.cash")}</option>
                  <option value="Bank">{t("common.bank")}</option>
                </select>
              </div>

              <div className="field pdField">
                <label className="label">{t("common.kind")}</label>
                <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} style={{ width: "100%" }}>
                  <option value="Advance">{t("partnerDisbursements.kindAdvanceFull")}</option>
                  <option value="Settlement">{t("partnerDisbursements.kindSettlementFull")}</option>
                  <option value="Other">{t("common.other")}</option>
                </select>
              </div>

              <div className="field pdField">
                <label className="label">
                  {t("common.date")} <span className="reqStar">*</span>
                </label>
                <input
                  className="input"
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div className="field pdField">
              <label className="label">{t("common.notes")}</label>
              <textarea
                className="input"
                placeholder={t("partnerDisbursements.notesPlaceholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ minHeight: 86, resize: "vertical" }}
              />
            </div>
          </div>

          {formError && (
            <div className="formError" role="alert">
              {formError}
            </div>
          )}

          <div className="formActions">
            <button className="btn" onClick={handleAdd} type="button">{t("partnerDisbursements.addBtn")}</button>
            <button className="btn btnSecondary" onClick={resetAddForm} type="button">{t("common.clear")}</button>
          </div>
        </Card>

        {/* TOTALS FILTERED */}
        <Card title={t("partnerDisbursements.totalsTitle")} subtitle={t("partnerDisbursements.totalsSub")}>
          <div
            style={{
              display: "flex",
              gap: 18,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div className="cardSub">{t("partnerDisbursements.kindAdvance")}</div>
              <div className="numWarn">{money(totalsFiltered.advance)}</div>
            </div>

            <div>
              <div className="cardSub">{t("partnerDisbursements.kindSettlement")}</div>
              <div className="numWarn">{money(totalsFiltered.settlement)}</div>
            </div>

            <div>
              <div className="cardSub">{t("partnerDisbursements.kindOther")}</div>
              <div className="numWarn">{money(totalsFiltered.other)}</div>
            </div>

            <div>
              <div className="cardSub">{t("common.cash")}</div>
              <div className="numWarn">{money(totalsFiltered.cash)}</div>
            </div>

            <div>
              <div className="cardSub">{t("common.bank")}</div>
              <div className="numWarn">{money(totalsFiltered.bank)}</div>
            </div>

            <div style={{ marginLeft: "auto" }}>
              <div className="cardSub">{t("common.total")}</div>
              <div className="numBad">{money(totalsFiltered.total)}</div>
            </div>
          </div>

          <div className="noteOnCard" style={{ marginTop: 8 }}>
            Note: Partner cash movements are tracked here (Advance/Settlement), but <b>project profit math comes from the engine</b>.
          </div>
        </Card>

        {/* LEDGER */}
        <div className="pdLedger">
          <TableShell
            title={t("partnerDisbursements.ledgerTitle")}
            subtitle={`${filteredDisbursements.length} rows • newest first`}
            right={ledgerHeaderRight}
          >
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>ID</th>
                  <th>{t("common.project")}</th>
                  <th>{t("partnerDisbursements.partner")}</th>
                  <th className="pdHideSm" style={{ width: 160 }}>
                    {t("common.kind")}
                  </th>
                  <th style={{ width: 130, textAlign: "right" }}>{t("common.amount")}</th>
                  <th className="pdHideSm" style={{ width: 110 }}>
                    {t("common.method")}
                  </th>
                  <th style={{ width: 120 }}>{t("common.date")}</th>
                  <th style={{ width: 260 }}>{t("common.notes")}</th>
                  <th style={{ width: 140 }}>{t("common.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {filteredDisbursements.map((d: any) => {
                  const isEditing = editingId === d.id;
                  const project = projects.find((p: any) => p.id === d.projectId);
                  const partner = partners.find((p: any) => p.id === d.partnerId);

                  return (
                    <tr key={d.id}>
                      <td>{d.id}</td>

                      <td>
                        {isEditing ? (
                          <select
                            value={editProjectId}
                            onChange={(e) => setEditProjectId(e.target.value)}
                            style={{ width: "100%" }}
                          >
                            <option value="">{t("common.selectProject")}</option>
                            {projects.map((p: any) => (
                              <option key={p.id} value={p.id}>
                                {projectLabel(p)}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <CellClamp lines={2} title={project ? projectLabel(project) : d.projectId}>
                            <span style={{ fontWeight: 900 }}>{project ? projectLabel(project) : d.projectId}</span>
                          </CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <select
                            value={editPartnerId}
                            onChange={(e) => setEditPartnerId(e.target.value)}
                            style={{ width: "100%" }}
                          >
                            <option value="">{t("partnerDisbursements.selectPartner")}</option>
                            {partners.map((p: any) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          partner?.name || d.partnerId
                        )}
                      </td>

                      <td className="pdHideSm">
                        {isEditing ? (
                          <select value={editKind} onChange={(e) => setEditKind(e.target.value as Kind)} style={{ width: "100%" }}>
                            <option value="Advance">{t("partnerDisbursements.kindAdvance")}</option>
                            <option value="Settlement">{t("partnerDisbursements.kindSettlement")}</option>
                            <option value="Other">{t("common.other")}</option>
                          </select>
                        ) : d.kind === "Settlement" ? (
                          <span className="badge badgeGood">{t("partnerDisbursements.kindSettlement")}</span>
                        ) : d.kind === "Other" ? (
                          <span className="badge">{t("partnerDisbursements.kindOther")}</span>
                        ) : (
                          <span className="badge badgeWarn">{t("partnerDisbursements.kindAdvance")}</span>
                        )}
                      </td>

                      <td style={{ textAlign: "right" }}>
                        {isEditing ? (
                          <input
                            className="input"
                            type="number"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            style={{ width: 130 }}
                          />
                        ) : (
                          <span className="numBad">{money(d.amount)}</span>
                        )}
                      </td>

                      <td className="pdHideSm">
                        {isEditing ? (
                          <select value={editMethod} onChange={(e) => setEditMethod(e.target.value as Method)} style={{ width: "100%" }}>
                            <option value="Cash">{t("common.cash")}</option>
                            <option value="Bank">{t("common.bank")}</option>
                          </select>
                        ) : (
                          <span className="badge">{d.method}</span>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <input className="input" type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                        ) : (
                          d.date
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <input className="input" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} style={{ width: "100%" }} />
                        ) : (
                          <CellClamp lines={2} title={d.notes || ""}>
                            {d.notes || "-"}
                          </CellClamp>
                        )}
                      </td>

                      <td>
                        {isEditing ? (
                          <div className="actionsRow">
                            <button className="btn btnSm" onClick={() => saveEdit(d)} type="button">{t("common.save")}</button>
                            <button className="btn btnSecondary btnSm" onClick={cancelEdit} type="button">{t("common.cancel")}</button>
                          </div>
                        ) : (
                          <div className="actionsRow" style={{ gap: 10 }}>
                            <button
                              type="button"
                              className="tableIconBtn"
                              onClick={() => startEdit(d)}
                              title={t("common.edit")} aria-label={t("partnerDisbursements.editAria")}
                            >
                              <IconPencil />
                            </button>
                            <button
                              type="button"
                              className="tableIconBtn tableIconBtnDanger"
                              onClick={() => doDelete(d)}
                              title={t("common.delete")} aria-label={t("partnerDisbursements.deleteAria")}
                            >
                              <IconTrash />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {filteredDisbursements.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: 14, color: "var(--muted)", fontStyle: "italic" }}>
                      {t("partnerDisbursements.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableShell>

          <div className="noteOnCard" style={{ marginTop: 10 }}>
            All-project totals: Advance {money(totalsAll.advance)} • Settlement {money(totalsAll.settlement)} • Other{" "}
            {money(totalsAll.other)} • Cash {money(totalsAll.cash)} • Bank {money(totalsAll.bank)}
          </div>
        </div>
      </div>
    </div>
  );
}

